import { io, Socket } from "socket.io-client";
import ApiUrls from "../api/ApiUrls";
import UtilityHelper from "../utilities/UtilityHelper";
import CacheStorage from "../storage/CacheStorage";
import StorageKeys from "../../const/StorageKeys";
import SessionStorage from "../storage/SessionStorage";
import SocketChatsKeys from "../../const/SocketChatsKeys";
import OnboardingProvider from "../../provider/onboarding/onboarding/OnboardingProvider";
import { ConversationList } from "../../model/chats/ChatListModel";
import Loader from "../../widget/common/ui/Loader";
import { HybridEncrypted } from "../encrypt_decrypt/EncryptDecryptService";
import ShowToast from "../../widget/common/ui/Toast";

ApiUrls.init();
const URL = ApiUrls.baseUrl2; // "http://localhost:4000"; // your backend URL
console.log("socket URL baseUrl2: ", ApiUrls.baseUrl2);

// Create and export the socket instance
export const socket: Socket = io(URL, {
  transports: ["websocket"],
  autoConnect: false, // connect manually when needed
  //reconnectionAttempts: 5, // Try to reconnect 5 times
  //reconnectionDelay: 1000, // Delay between reconnection attempts
  //reconnectionDelayMax: 5000, // Max delay between attempts
  query: { apiVersion: "v123" },
});

export async function safeEmitWithAck<T = any, R = any>(
  socket: Socket,
  event: string,
  data?: T
): Promise<R | null> {
  if (!socket.connected) {
    console.warn(`[socket] Tried to emit '${event}' while disconnected`);
    return null;
  }

  try {
    const response = await socket.emitWithAck<any>(event, data);
    return response;
  } catch (err: any) {
    if (err?.message?.includes("disconnected")) {
      console.debug(`[socket] '${event}' failed — socket disconnected`);
      return null;
    }
    console.error(`[socket] '${event}' failed:`, err);
    return null;
  }
}

const SocketService = new (class SocketService {
  static accountId: string = "";
  static accountName: string = "";
  static conversationId: string = "";
  static chatFlag: string = ""; //1=team,2=dept, 3=group, 4= group team member

  static onAnyCallback = (event: string, ...args: any[]) => {
    console.log(`Received event: ${event}`, args);
  };

  disconnect() {
    socket.off("connect");
    socket.off("connect_error");
    socket.off("disconnect");
    socket.off("reconnect");
    socket.off("reconnect_attempt");
    socket.off("reconnect_failed");

    socket.off("authenticate_socket");
    socket.off("authentication_expired");

    // Attach listener to all events
    socket.offAny(SocketService.onAnyCallback);

    socket.disconnect();
  }

  disconnectCallBacks(callback: (event: string, ...args: any[]) => void) {
    console.log(`callback....`);
    socket.offAny(callback);
  }

  initialize = async (): Promise<boolean> => {
    const status: boolean = await new Promise((resolve, reject) => {
      const onConnect = async () => {
        socket.off("connect", onConnect); // cleanup

        console.log("Connected to server");
        console.log(`scoket status: ${socket.connected}`);
        console.log(`socket id : ${socket.id!}`);

        this.callAuthenticate();
        // await UtilityHelper.delay(500);
        // await Future.delayed(const Duration(milliseconds: 500));
        // PushNotificationService().handlePushNotifications(context: context);
        resolve(true);
      };

      const onConnectError = (err: any) => {
        socket.off("connect_error", onConnectError);
        resolve(false);
        reject(err);
      };

      socket.on("connect", onConnect);
      socket.on("connect_error", onConnectError);

      socket.connect(); // start connection if autoConnect is false
    });

    if (status) {
      socket.on("reconnect", (attempt) => {
        console.log(`✅ Successfully reconnected after ${attempt} attempts`);
      });

      socket.on("reconnect_attempt", (attempt) => {
        console.log(`🔁 Reconnect attempt ${attempt}...`);
      });

      socket.on("reconnect", () => {
        console.log("Disconnected from server");
      });

      socket.on("reconnect_failed", () => {
        console.error("🚫 Could not reconnect to server");
      });

      socket.on("disconnect", (reason) => {
        console.warn("❌ Disconnected:", reason);
      });

      // Attach listener to all events
      socket.onAny(SocketService.onAnyCallback);

      ///////////////////////////////////////////////////////////////////
      socket.on("authenticate_socket", () => {
        console.log("Authenticate socket from server");
      });

      socket.on("authentication_expired", async () => {
        console.log("Authentication expired from server");
        await OnboardingProvider.refreshToken();
        await UtilityHelper.delay(500);
        this.callAuthenticate();
      });

      return true;
    }

    return false;
  };

  callAuthenticate = async () => {
    const apiToken: string = CacheStorage.getValue(StorageKeys.api_token);
    const accountId: string =
      CacheStorage.getValue(StorageKeys.account_id) || "0";
    if (
      UtilityHelper.isNotEmpty(apiToken) &&
      UtilityHelper.isNotEmpty(accountId)
    ) {
      const response = await safeEmitWithAck(socket, "authenticate_socket", {
        token: apiToken,
        account_id: accountId,
        app_type: "2",
      });
      if (response != null && response["status"] === "1") {
        console.log(`callAuthenticate sucess: `, response);
      }
    }
  };

  disconnectProfile = () => {
    /* print("calling disconnect : start");
    await Future.delayed(const Duration(milliseconds: 500));
    socket.emitWithAck(
      "disconnect_profile",
      {},
      ack: (val) async {
        print(" ---------------- disconnect profile emit --------------------------- ");
        print(val);
        if (init) {
          CachedStorage().saveValue(key: "socket_init", value: "1");
        } else {
          socket.offAny(onAnyCallback);
        }
      },
    );

    print("calling disconnect : end"); */
  };

  leaveChat = async () => {
    console.log(`leaveChat`);
    const chatsChatSelectedSession = SessionStorage.getValue(
      StorageKeys.chats_chat_selected
    );

    if (UtilityHelper.isNotEmpty(chatsChatSelectedSession)) {
      const chatSelected: ConversationList = JSON.parse(
        chatsChatSelectedSession
      );
      console.log(chatSelected);

      //SessionStorage.deleteValue(StorageKeys.chats_chat_selected);

      const chatFlag: string = chatSelected?.flag!;
      const emitEvent =
        chatFlag === "3"
          ? SocketChatsKeys.user_left_chat_group
          : SocketChatsKeys.leave_chat;

      const response = await safeEmitWithAck(socket, emitEvent, {
        conv_id: chatSelected?.conv_id!,
      });
      if (response != null && response["status"] === "1") {
        console.log("Leaved successfully");
        //SessionStorage.deleteValue(StorageKeys.chats_chat_selected);
      }
    }
  };

  initConversation = async ({
    eventN,
    flag,
    profId = "",
    branchId = "",
    convId = "",
    forward = false,
    groupName = "",
    broadcastName = "",
    membersList = "",
    screen = false,
    socketCallBack,
    callback,
  }: {
    eventN: string;
    flag: string;
    profId?: string;
    branchId?: string;
    convId?: string;
    forward?: boolean;
    groupName?: string;
    broadcastName?: string;
    membersList?: string;
    screen?: boolean;
    socketCallBack?: (event: string, ...args: any[]) => void;
    callback?: (res: any) => void;
  }) => {
    console.log(`initConversation`);
    console.log(`flag: ${flag}, event: ${eventN}`);

    const chatFlag: string = flag;
    const emitEvent: string = eventN;

    let params: {
      conv_id?: string;
      flag?: string;
      team_account_id?: string;
      branch_id?: string;
      group_id?: string;
      department_id?: string;
      group_conv_id?: string;
    } = {};

    if (eventN === SocketChatsKeys.join_chat) {
      params = { conv_id: convId, flag: chatFlag };
    } else if (eventN === SocketChatsKeys.join_chat_group) {
      params = { conv_id: convId };
    } else if (eventN === SocketChatsKeys.request_user_group_chat) {
      params = { group_id: profId, group_conv_id: convId };
    } else if (eventN === SocketChatsKeys.check_team_contact_request) {
      params =
        chatFlag === "4"
          ? {
              team_account_id: profId,
              branch_id: branchId,
              flag: chatFlag,
              group_id: convId,
            }
          : {
              team_account_id: profId,
              branch_id: branchId,
              flag: chatFlag,
            };
    } else if (eventN === SocketChatsKeys.check_dept_contact_request) {
      params = { department_id: profId, branch_id: branchId };
    }

    console.log(`body: `, params);

    //if ("conv_id" in params) {
    //Loader.modal();
    const response = await safeEmitWithAck(socket, emitEvent, params);
    // Loader.modal(false);
    console.log(response);

    if (response != null) {
      if (response["status"] === "1") {
        if (socketCallBack !== undefined) {
          socket.onAny(socketCallBack);
        }
      }

      if (callback !== undefined) {
        callback(response);
      }
    }
    //}
  };

  readMessage = async (convId: string, chatFlag: string) => {
    if (
      UtilityHelper.isNotEmpty(convId) &&
      UtilityHelper.isNotEmpty(chatFlag)
    ) {
      const response = await safeEmitWithAck(
        socket,
        chatFlag === "3"
          ? SocketChatsKeys.mark_read_chat_group_message
          : SocketChatsKeys.mark_read_chat,
        {
          conv_id: convId,
        }
      );
      if (response != null && response["status"] === "1") {
        console.log(response);
      }
    }
  };

  sendTeamMessage = async ({
    convId,
    flag,
    message,
    messageType,
    callback,

    hybridMessage,
    fileId = "",
    replyId = "",
    forwardStatus = "0",
    isBroadcast = false,
    callbackBraodcast,
  }: {
    convId: string;
    flag: string;
    message: Record<string, string>;
    messageType: string;
    callback: () => void;
    hybridMessage?: Record<string, HybridEncrypted>;
    fileId?: string;
    replyId?: string;
    forwardStatus?: string;
    isBroadcast?: boolean;
    callbackBraodcast?: (val: string) => void;
  }) => {
    console.log(`sendTeamMessage`);
    if (!["1", "9"].includes(messageType)) {
      Loader.modal();
    }

    const params: any = {
      conv_id: convId,
      encryption_type: hybridMessage != null ? "hybrid" : "e2e",
      message: hybridMessage ?? message,
      message_type: messageType,
      file_id: fileId,
      replay_id: replyId,
      forward_status: forwardStatus,
      flag: flag,
    };

    console.log(params);
    const response = await safeEmitWithAck(
      socket,
      SocketChatsKeys.send_chat_message,
      params
    );

    Loader.modal(false);
    if (response != null && response["status"] === "1") {
      console.log(response);
      if (callback !== undefined) {
        callback();
      }
    }
  };

  sendDeptMessage = async ({
    convId,
    flag,
    message,
    messageType,
    messageTag,
    messageIv,
    callback,

    fileId = "",
    replyId = "",
    forwardStatus = "0",
  }: {
    convId: string;
    flag: string;
    message: string;
    messageType: string;
    messageTag: string;
    messageIv: string;

    callback: () => void;
    fileId?: string;
    replyId?: string;
    forwardStatus?: string;
  }) => {
    console.log(`sendDeptMessage`);
    if (!["1", "9"].includes(messageType)) {
      Loader.modal();
    }

    const response = await safeEmitWithAck(
      socket,
      SocketChatsKeys.send_dept_chat_message,
      {
        conv_id: convId,
        message: message,
        encryption_type: "symmetric",
        message_type: messageType,
        file_id: fileId,
        message_iv: messageIv,
        message_tag: messageTag,
        replay_id: replyId,
        forward_status: forwardStatus,
        app_flag: flag,
      }
    );

    Loader.modal(false);
    if (response != null && response["status"] === "1") {
      console.log(response);
      if (callback !== undefined) {
        callback();
      }
    }
  };

  sendGroupMessage = async ({
    convId,
    flag,
    message,
    messageType,
    messageTag,
    messageIv,
    members,
    callback,

    fileId = "",
    replyId = "",
    forwardStatus = "0",
  }: {
    convId: string;
    flag: string;
    message: string;
    messageType: string;
    messageTag: string;
    messageIv: string;
    members: string;

    callback: () => void;
    fileId?: string;
    replyId?: string;
    forwardStatus?: string;
  }) => {
    if (!["1", "9"].includes(messageType)) {
      Loader.modal();
    }

    const response = await safeEmitWithAck(
      socket,
      SocketChatsKeys.send_chat_group_message,
      {
        conv_id: convId,
        message: message,
        encryption_type: "symmetric",
        message_type: messageType,
        file_id: fileId,
        message_iv: messageIv,
        message_tag: messageTag,
        members_list: members,
        replay_id: replyId,
        forward_status: forwardStatus,
      }
    );

    Loader.modal(false);
    if (response != null && response["status"] === "1") {
      console.log(response);
      if (callback !== undefined) {
        callback();
      }
    }
  };

  updateConversationSettings = async ({
    flag,
    convId,
    eventN,
    exitFlag = "",
    muteType = "",
    muteStatus = "",
    muteDuration = "",
    profileId = "",
    blockStatus = "",
    callback,
  }: {
    flag: string;
    convId: string;
    eventN: string;
    exitFlag?: string;
    muteType?: string;
    muteStatus?: string;
    muteDuration?: string;
    profileId?: string;
    blockStatus?: string;
    callback?: (res: any) => void;
  }) => {
    console.log(`updateConversationSettings`);
    console.log(`flag: ${flag}, event: ${eventN}`);

    const chatFlag: string = flag;
    const emitEvent: string = eventN;

    let params: {
      conv_id?: string;
      flag?: string;
      profile_id?: string;
      block_status?: string;
      mute_status?: string;
      mute_duration?: string;
      mute_type?: string;
    } = {};

    if (eventN === SocketChatsKeys.chat_clear_messages) {
      params = { conv_id: convId, flag: chatFlag };
    } else if (eventN === SocketChatsKeys.chat_block_profile) {
      params = {
        conv_id: convId,
        profile_id: profileId,
        block_status: blockStatus,
        flag: chatFlag,
      };
    } else if (eventN === SocketChatsKeys.user_exit_chat_group) {
      params = { conv_id: convId, flag: exitFlag };
    } else if (eventN === SocketChatsKeys.chat_mute_notification) {
      params = {
        conv_id: convId,
        mute_status: muteStatus,
        mute_duration: muteDuration,
        mute_type: muteType,
        flag: chatFlag,
      };
    }

    console.log(`body: `, params);

    if ("conv_id" in params) {
      Loader.modal();
      const response = await safeEmitWithAck(socket, emitEvent, params);
      Loader.modal(false);
      if (response != null && response["status"] === "1") {
        console.log(response);

        if (callback !== undefined) {
          callback(response);
        }
      }
    }
  };

  deleteMessage = async ({
    flag,
    convId,
    msgIDs,
    status,
    callback,
  }: {
    flag: string;
    convId: string;
    msgIDs: string;
    status: string;
    callback?: (res: any) => void;
  }) => {
    const params: {
      conv_id?: string;
      flag?: string;
      message_ids?: string;
      status?: string;
    } = {
      conv_id: convId,
      flag: flag,
      message_ids: msgIDs,
      status: status,
    };

    console.log(`deleteMessage`);
    console.log(params);

    if ("conv_id" in params) {
      const response = await safeEmitWithAck(
        socket,
        SocketChatsKeys.chat_delete_messages,
        params
      );

      console.log(response);
      if (response != null) {
        if (callback !== undefined) {
          callback(response);
        }

        if (response["status"] === "0") {
          ShowToast.error(response["message"]);
        }
      }
    }
  };

  typingChatMessage = async ({
    flag,
    convId,
    name = CacheStorage.getValue(StorageKeys.name),
    status,
    callback,
  }: {
    flag: string;
    convId: string;
    name?: string;
    status: string;
    callback?: (res: any) => void;
  }) => {
    const params: {
      conv_id?: string;
      name?: string;
      typing_status?: string;
      typing_flag?: string;
    } = {
      conv_id: convId,
      name: name,
      typing_status: status,
      typing_flag: flag,
    };

    console.log(`typingChatMessage`);
    console.log(params);

    if ("conv_id" in params) {
      const response = await safeEmitWithAck(
        socket,
        SocketChatsKeys.profile_typing,
        params
      );
      if (response != null && response["status"] === "1") {
        console.log(response);

        if (callback !== undefined) {
          callback(response);
        }
      }
    }
  };

  arfDepartmentMessages = async ({
    convId = "",
    callback,
  }: {
    convId: string;
    callback?: (res: any) => void;
  }) => {
    if (UtilityHelper.isNotEmpty(convId)) {
      const params: {
        conv_id?: string;
      } = {
        conv_id: convId,
      };

      const response = await safeEmitWithAck(
        socket,
        SocketChatsKeys.chat_message_list,
        params
      );
      if (response != null && response["status"] === "1") {
        console.log(response);

        if (callback !== undefined) {
          callback(response);
        }
      }
    }
  };
})();

export default SocketService;
